import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { Language, t as translate } from '@/lib/i18n';
import { supabase } from '@/integrations/supabase/client';
import { useAppContext } from '@/contexts/AppContext';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => Promise<void>;
  t: (key: string, vars?: Record<string, string | number>) => string;
}

const LanguageContext = createContext<LanguageContextType | null>(null);

const STORAGE_KEY = 'sola_language';

const isLanguage = (val: unknown): val is Language =>
  val === 'pt' || val === 'en' || val === 'es';

export function LanguageProvider({ children }: { children: ReactNode }) {
  const { user, profile } = useAppContext();

  const [language, setLang] = useState<Language>(() => {
    if (typeof window === 'undefined') return 'pt';
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return isLanguage(stored) ? stored : 'pt';
  });

  useEffect(() => {
    const profLang = (profile as any)?.language;
    if (isLanguage(profLang)) {
      setLang(profLang);
      try {
        window.localStorage.setItem(STORAGE_KEY, profLang);
      } catch {}
    }
  }, [profile]);

  useEffect(() => {
    if (!user || profile) return;
    let cancelled = false;
    supabase
      .from('profiles')
      .select('language')
      .eq('user_id', user.id)
      .single()
      .then(({ data }) => {
        if (cancelled) return;
        const lang = (data as any)?.language;
        if (isLanguage(lang)) {
          setLang(lang);
          try {
            window.localStorage.setItem(STORAGE_KEY, lang);
          } catch {}
        }
      });
    return () => {
      cancelled = true;
    };
  }, [user, profile]);

  const setLanguage = async (lang: Language) => {
    setLang(lang);
    try {
      window.localStorage.setItem(STORAGE_KEY, lang);
    } catch {}
    if (user) {
      await supabase
        .from('profiles')
        .update({ language: lang })
        .eq('user_id', user.id);
    }
  };

  const tFn = (key: string, vars?: Record<string, string | number>) =>
    translate(language, key, vars);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t: tFn }}>
      {children}
    </LanguageContext.Provider>
  );
}

export const useLanguage = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLanguage must be used inside LanguageProvider');
  return ctx;
};